import { BaseFieldClass } from './baseField.class'

import { formatDateTimeApi, formatDateTimeHuman, formatDateApi, formatDateHuman } from '@/api/formatter'
import { FieldOptions } from '@/models/fields/fieldOptions.class.ts'
import { IDateField, IFieldOptions } from '@/types/serverField.type'

const EMPTY_DATE = '0001-01-01T00:00:00Z'

export class DateFieldClass extends BaseFieldClass {
  declare fieldValue: Date | string | null
  declare initialValue: Date | string | null
  datetimeOptions: DatetimeOptions | null

  // Поле time хранит время как строку в формате ЧЧ:мм, а не как объект Date.
  private get isTime (): boolean {
    return this.widgetType === 'time'
  }

  // Поле datetime хранит дату и время как объект Date.
  private get isDateTime (): boolean {
    return this.widgetType === 'datetime'
  }

  constructor (field: IDateField) {
    super(field)
    const value = !field.Value || field.Value === EMPTY_DATE
      ? field.DefaultValue
      : field.Value
    this.datetimeOptions = new DatetimeOptions(field.Options)
    this.initialValue = this.prepareValue(value as string | Date | null)
    this.fieldValue = this.prepareValue(value as string | Date | null)
  }

  prepareValue (value: string | Date | null): Date | string | null {
    if (!value || value === EMPTY_DATE) return null
    // Время сохраняем как есть — строка ЧЧ:мм, без преобразования в Date.
    if (this.isTime && typeof value === 'string') return value
    return value instanceof Date ? value : new Date(value)
  }

  getValue (): Record<string, string> {
    if (this.isTime) {
      return { [this.name]: this.fieldValue ? String(this.fieldValue) : '' }
    }
    if (this.isDateTime) {
      return {
        [this.name]: this.fieldValue ? formatDateTimeApi(this.fieldValue as Date) : ''
      }
    }
    return {
      [this.name]: this.fieldValue ? formatDateApi(this.fieldValue as Date) : ''
    }
  }

  isEmpty (): boolean {
    return !this.value
  }

  appendToFormData (formData: FormData): void {
    if (this.isTime) {
      formData.append(this.name, this.fieldValue ? String(this.fieldValue) : '')
      return
    }
    if (this.isDateTime) {
      formData.append(this.name, this.fieldValue ? formatDateTimeApi(this.fieldValue as Date) : '')
      return
    }
    formData.append(this.name, this.fieldValue ? formatDateApi(this.fieldValue as Date) : '')
  }

  getFilterValue (): string | null {
    if (this.isTime) {
      return this.fieldValue ? String(this.fieldValue) : null
    }
    if (this.isDateTime) {
      return this.fieldValue ? formatDateTimeApi(this.fieldValue as Date) : null
    }
    return this.fieldValue ? formatDateApi(this.fieldValue as Date) : null
  }

  getValueAsText (): string {
    if (this.isTime) {
      return this.fieldValue ? String(this.fieldValue) : ''
    }
    if (this.isDateTime || this.datetimeOptions?.attrs?.showTime) {
      return this.fieldValue ? formatDateTimeHuman(this.fieldValue as Date) : ''
    }
    return this.fieldValue ? formatDateHuman(this.fieldValue as Date) : ''
  }
}

// DatetimeOptions расширяет класс FieldOptions для использования дополнительных атрибутов,
// полученных от сервера для полей с датой и временем.
class DatetimeOptions extends FieldOptions {
  attrs: Record<string, unknown>

  constructor (options: IFieldOptions) {
    super(options)
    this.attrs = options?.Attrs ?? {}
  }
}
